import { BusinessObjectBase } from "./base";
import { roleAssignmentSchema } from "@schemas/business-objects/role-assignment";
import type { Role } from "./role";
import type { User } from "./user";
import type { Id } from "../base-types";

export interface RoleAssignmentData {
  readonly id: Id;
  readonly userId: Id;
  readonly user?: User;
  readonly roleId: Id;
  readonly role?: Role;
}

export interface RoleAssignment extends Readonly<RoleAssignmentData> {}
type RoleAssignmentCreateWithChildrenData = Partial<RoleAssignmentData>;

export class RoleAssignment extends BusinessObjectBase {
  static readonly entityName = "RoleAssignment" as const;
  static readonly plural = "role-assignments";
  static schema = roleAssignmentSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<RoleAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new RoleAssignment(data));
  }

  static async findOne(filter?: any): Promise<RoleAssignment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new RoleAssignment(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<RoleAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params["filter"] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new RoleAssignment(data);
  }

  static async create(
    data: Omit<RoleAssignmentData, "id">,
  ): Promise<RoleAssignment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new RoleAssignment(result);
  }

  static async createMany(
    data: Omit<RoleAssignmentData, "id">[],
  ): Promise<RoleAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new RoleAssignment(item));
  }

  static async createWithChildren(
    data: RoleAssignmentCreateWithChildrenData,
  ): Promise<RoleAssignment> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new RoleAssignment(result);
  }

  static async createManyWithChildren(
    data: RoleAssignmentCreateWithChildrenData[],
  ): Promise<RoleAssignment[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new RoleAssignment(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<RoleAssignmentData>,
  ): Promise<RoleAssignment> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new RoleAssignment(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
